// src/login/AnimatedVehicles.jsx
import { Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import DirectionsBusFilledIcon from '@mui/icons-material/DirectionsBusFilled';

// Animações base
const moveRight = keyframes`
  0%   { transform: translateX(-12vw); }
  100% { transform: translateX(112vw); }
`;
const moveLeft = keyframes`
  0%   { transform: translateX(112vw); }
  100% { transform: translateX(-12vw); }
`;

// Componente utilitário para um veículo
function Vehicle({
  children,
  bottom,
  direction = 'right',
  duration = 18,
  delay = 0,
  flip = false, // vira o ícone para “olhar” no sentido de viagem
  glow,
}) {
  const initialTranslate = direction === 'right' ? '-12vw' : '112vw';
  const common = {
    position: 'absolute',
    bottom,
    display: 'flex',
    alignItems: 'center',
    willChange: 'transform',
    filter: `drop-shadow(0 0 8px ${glow})`,
    animation: `${direction === 'right' ? moveRight : moveLeft} ${duration}s linear infinite`,
    animationDelay: typeof delay === 'number' ? `${delay}s` : delay,
    animationFillMode: 'both',
    transform: `translateX(${initialTranslate})`,
  };

  // Para iniciar do lado correto
  const sidePos = direction === 'right' ? { left: 0 } : { right: 0 };

  return (
    <Box sx={{ ...common, ...sidePos }}>
      {/* wrapper separa o flip da animação */}
      <Box sx={{ transform: flip ? 'scaleX(-1)' : 'none' }}>{children}</Box>
    </Box>
  );
}

export default function AnimatedVehicles() {
  const theme = useTheme();

  const roadBg = alpha(theme.palette.common.black, 0.22);
  const laneColor = alpha(theme.palette.common.white, 0.22);
  const glow = alpha(theme.palette.secondary.main, 0.25);

  // Paleta
  const carColor = theme.palette.secondary.main;
  const truckColor = theme.palette.primary.main;
  const motoColor = theme.palette.info?.main || theme.palette.secondary.light;
  const busColor = theme.palette.warning?.main || theme.palette.secondary.light;

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        height: { xs: 72, sm: 80 },
        zIndex: 1,
        pointerEvents: 'none',
        background: roadBg,
        backdropFilter: 'blur(2px)',
        borderTop: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
        boxShadow: `0 -6px 24px ${alpha(theme.palette.common.black, 0.4)} inset`,
        '@media (prefers-reduced-motion: reduce)': { display: 'none' },
      }}
    >
      {/* faixa tracejada central */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '50%',
          height: 2,
          transform: 'translateY(-50%)',
          backgroundImage: `repeating-linear-gradient(90deg, ${laneColor} 0 24px, transparent 24px 48px)`,
        }}
      />

      {/* ====== LANE 1 (baixo) — ESQUERDA → DIREITA ====== */}
      <Vehicle bottom={10} direction="right" duration={18} delay={0} glow={glow}>
        {/* carro frontal (mantém orientação) */}
        <DirectionsCarFilledIcon sx={{ fontSize: 32, color: carColor }} />
      </Vehicle>
      {/* outro carro na mesma pista, atraso para variar */}
      <Vehicle bottom={10} direction="right" duration={18} delay={9} glow={glow}>
        <DirectionsCarFilledIcon sx={{ fontSize: 30, color: carColor }} />
      </Vehicle>

      {/* ====== LANE 2 (meio-baixa) — DIREITA → ESQUERDA ====== */}
      <Vehicle
        bottom={28}
        direction="left"
        duration={24}
        delay={1.4}
        glow={glow}
        flip
      >
        {/* caminhão (ícone de perfil, precisa flip pra “olhar” pra esquerda) */}
        <LocalShippingIcon sx={{ fontSize: 40, color: truckColor }} />
      </Vehicle>
      {/* ônibus também vindo da direita */}
      <Vehicle
        bottom={28}
        direction="left"
        duration={26}
        delay={12}
        glow={glow}
        flip
      >
        <DirectionsBusFilledIcon sx={{ fontSize: 38, color: busColor }} />
      </Vehicle>

      {/* ====== LANE 3 (meio-alta) — ESQUERDA → DIREITA ====== */}
      <Vehicle bottom={46} direction="right" duration={14} delay={2.8} glow={glow}>
        <TwoWheelerIcon sx={{ fontSize: 28, color: motoColor }} />
      </Vehicle>
      {/* ônibus indo para a direita também */}
      <Vehicle bottom={46} direction="right" duration={22} delay={10.5} glow={glow}>
        <DirectionsBusFilledIcon sx={{ fontSize: 36, color: busColor }} />
      </Vehicle>

      {/* ====== LANE 4 (alta) — DIREITA → ESQUERDA ====== */}
      <Vehicle bottom={64} direction="left" duration={20} delay={4.2} glow={glow} flip>
        <TwoWheelerIcon sx={{ fontSize: 26, color: motoColor }} />
      </Vehicle>
      <Vehicle bottom={64} direction="left" duration={23} delay={15} glow={glow} flip>
        <LocalShippingIcon sx={{ fontSize: 38, color: truckColor }} />
      </Vehicle>
    </Box>
  );
}
